import { ErrorBoundary, JSX, onMount } from "solid-js";
import { IntlMessageFormat } from "intl-messageformat";

import Telemetry from "@repo/telemetry";
import Locale from "@repo/utils/Locale";
import UObject from "@repo/utils/UObject";

declare namespace Trans {
  type Props = {
    locales: Record<string, string>;
    defaultMessage?: string;
    components?: ((children: JSX.Element) => JSX.Element)[];
    expressions?: Record<string, () => any>;
  };
}
function Trans({
  locales,
  defaultMessage,
  components = [],
  expressions,
}: D<Trans.Props>) {
  return (
    <ErrorBoundary
      fallback={(err) => {
        onMount(() => {
          Telemetry.captureError(err);
        });

        return "Invalid translation";
      }}
    >
      {(() => {
        const message =
          locales?.[{ en: "en", fr: "fr_CA", es: "es" }[Locale.current]] ||
          defaultMessage;

        if (!message)
          throw new Error(
            "Locale doesn't exist and defaultMessage not provided"
          );

        const render = (message: string) =>
          [...JsxMessageFormat.parse(message)].map(
            function next(node): JSX.Element {
              return typeof node === "string" ? (
                new IntlMessageFormat(node).format(
                  UObject.transform(expressions || {}, ([key, value]) => [
                    key,
                    value(),
                  ])
                )
              ) : (
                <>{components[node.name as any]!(node.children.map(next))}</>
              );
            }
          );

        return (
          <ErrorBoundary
            fallback={(err) => {
              onMount(() => {
                Telemetry.captureError(err);
              });

              return render(locales?.en || defaultMessage!);
            }}
          >
            {render(message)}
          </ErrorBoundary>
        );
      })()}
    </ErrorBoundary>
  );
}
export default Trans;

namespace JsxMessageFormat {
  /**
   * @param {string} message
   */
  export const tokenize = (message: string) => message.split(/(<.*?>)/g);

  /**
   * @param {string} message
   */
  export function parse(message: string) {
    const tokens = JsxMessageFormat.tokenize(message);
    const iter = tokens[Symbol.iterator]();

    type Node = string | { name: string; children: Node[] };

    return (function* next(): Generator<Node> {
      for (const value of iter) {
        if (value[0] === "<") {
          // Close tag
          if (value[1] === "/") return;
          // Self closing
          if (value.endsWith("/>")) {
            yield { name: value.slice(1, -2), children: [] };
            continue;
          }
          yield { name: value.slice(1, -1), children: [...next()] };
        } else yield value;
      }
    })();
  }
}
